import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import usePrevious from 'react-use/lib/usePrevious';
import { isEqual } from 'lodash';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { UserSelectors } from 'store/User';
import * as UserThunks from 'store/User/thunks';
import {
  isSupportedCustomNamespace,
  isSupportedLocale,
  SUPPORTED_CUSTOM_NAMESPACES,
  SUPPORTED_LOCALES,
} from 'util/localization/constants';

import i18n from './init';

function useCustomTranslations() {
  const customTranslations = useAppSelector((state) => {
    const featureFlags = UserSelectors.selectFeatureFlags(state);
    return featureFlags.kds_custom_translations;
  });

  return customTranslations as object;
}

function resetCustomTranslations() {
  SUPPORTED_LOCALES.forEach((locale) => {
    SUPPORTED_CUSTOM_NAMESPACES.forEach((namespace) => {
      i18n.removeResourceBundle(locale, namespace);
    });
  });
}

async function loadCustomTranslations(customTranslations: object) {
  for (const [languageTag, translations] of Object.entries(
    customTranslations,
  )) {
    if (isSupportedLocale(languageTag)) {
      for (const [namespace, resources] of Object.entries(translations)) {
        if (isSupportedCustomNamespace(namespace)) {
          i18n.addResourceBundle(languageTag, namespace, resources, true, true);
        }
      }
    }
  }
  await i18n.reloadResources();
}

export function useManageCustomTranslations() {
  const customTranslations = useCustomTranslations();
  const customTranslationsPrev = usePrevious(customTranslations);

  useEffect(() => {
    if (!customTranslations && !customTranslationsPrev) {
      return;
    }

    if (!isEqual(customTranslations, customTranslationsPrev)) {
      loadCustomTranslations(customTranslations);
    }
  }, [customTranslations, customTranslationsPrev]);

  useEffect(() => {
    return () => resetCustomTranslations();
  }, []);
}

export function useSyncLocale() {
  const dispatch = useAppDispatch();

  const {
    i18n: { resolvedLanguage },
  } = useTranslation();

  useEffect(() => {
    if (resolvedLanguage) {
      dispatch(UserThunks.update({ locale: resolvedLanguage }));
    }
  }, [dispatch, resolvedLanguage]);
}
